import { CopyableId, EffectiveValue, ErrorState, LoadingState, Stat, StatusPill } from '../../components';
import { formatCents, formatCount, formatRate } from '../../lib/format/money';
import { formatDateTime } from '../../lib/format/dates';
import { localeLabel } from '../locales/api';
import { useLocalesById } from '../locales/queries';
import { humanize, orderStatusTone } from '../profiles/people';
import type { OrderDetail as OrderDetailData, TransactionSnapshot } from './api';
import { Field, FieldGrid, KeyValues, Ledger, Section, formatAddress, isEmptyObject } from './detailParts';
import { useOrderDetail } from './queries';

/**
 * Order Detail — every order field plus the money snapshot written at capture
 * (`GET /admin/orders/{id}/detail`). The snapshot records the values in force
 * at TAX-CALC time; it is absent before capture and for cancellation-fee-only
 * captures.
 */
export function OrderDetail({ orderId }: { orderId: string }) {
  const detail = useOrderDetail(orderId);
  if (detail.isPending) return <LoadingState label="Loading order…" />;
  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} compact />;
  return <OrderDetailBody detail={detail.data} />;
}

function OrderDetailBody({ detail }: { detail: OrderDetailData }) {
  const { order, eater, chef, snapshot } = detail;

  return (
    <div>
      <div className="mb-[18px] flex items-start gap-4 pr-8">
        <div className="min-w-0 flex-1">
          <div className="font-heading text-2xl font-bold tracking-[-.02em] text-brown-deep">
            Order <span className="font-mono">{order.orderNumber}</span>
          </div>
          <div className="mt-[3px] text-[13px] text-muted">
            {eater?.displayName || 'Unknown eater'} ordered from {chef?.displayName || 'unknown chef'}
          </div>
          <div className="mt-0.5">
            <CopyableId id={order.id} label="Order ID" />
          </div>
        </div>
        <StatusPill tone={orderStatusTone(order.status)} dot className="shrink-0">
          {humanize(order.status)}
        </StatusPill>
      </div>

      <div className="grid grid-cols-4 gap-2.5">
        <Stat label="Quantity" value={formatCount(order.quantity)} />
        <Stat label="Price / unit" value={formatCents(order.pricePerUnit)} />
        <Stat label="Subtotal" value={formatCents(order.subtotal)} />
        <Stat label="Total paid" value={formatCents(order.totalPaid)} hint="after tax and fees" />
      </div>

      <Section title="Order">
        <FieldGrid>
          <Field label="Placed">{formatDateTime(order.createdAt)}</Field>
          <Field label="Fulfillment">{humanize(order.fulfillmentMethod)}</Field>
          <Field label="Sale date">{order.saleDate}</Field>
          <Field label="Intended pickup">{order.intendedPickupTime}</Field>
          <Field label="Held until">{order.heldUntil && formatDateTime(order.heldUntil)}</Field>
          <Field label="Completed">{order.completedAt && formatDateTime(order.completedAt)}</Field>
          <Field label="Eater confirmed">{order.ordererConfirmedAt && formatDateTime(order.ordererConfirmedAt)}</Field>
          <Field label="Chef confirmed">{order.listerConfirmedAt && formatDateTime(order.listerConfirmedAt)}</Field>
          <Field label="On my way">{order.onMyWayAt && formatDateTime(order.onMyWayAt)}</Field>
          <Field label="Chef nudged">{order.listerNudgedAt && formatDateTime(order.listerNudgedAt)}</Field>
          <Field label="Pickup reminder sent">
            {order.pickupReminderSentAt && formatDateTime(order.pickupReminderSentAt)}
          </Field>
          <Field label="Ready prompt sent">{order.readyPromptSentAt && formatDateTime(order.readyPromptSentAt)}</Field>
          <Field label="Late reminder stage">{String(order.lateReminderStage)}</Field>
          <Field label="Verification code">
            <span className="font-mono">{order.verificationCode}</span>
          </Field>
          <Field label="Stripe payment">
            {order.stripePaymentId && <CopyableId id={order.stripePaymentId} label="Stripe payment ID" />}
          </Field>
          <Field label="Rejection reason">{order.rejectionReason}</Field>
          <Field label="Order address" wide>
            {formatAddress(order.orderAddress)}
          </Field>
        </FieldGrid>
      </Section>

      {snapshot ? (
        <MoneySnapshot snapshot={snapshot} />
      ) : (
        <Section title="Money snapshot">
          <div className="text-[13px] text-muted">
            No money snapshot. One is written when the order is captured, and not for cancellation-fee-only captures.
          </div>
        </Section>
      )}
    </div>
  );
}

function MoneySnapshot({ snapshot: s }: { snapshot: TransactionSnapshot }) {
  const [locale] = useLocalesById(s.localeIdAtCalc ? [s.localeIdAtCalc] : []);

  return (
    <>
      <Section title={`Money snapshot · ${humanize(s.transactionType)}`}>
        <Ledger
          rows={[
            { label: 'Gross', hint: 'owed to chef', value: formatCents(s.grossCents) },
            { label: 'Tax', value: formatCents(s.taxCents) },
            { label: 'Stripe fee', value: formatCents(s.stripeFeeCents) },
            { label: 'Buffer', value: formatCents(s.bufferCents) },
            { label: 'RDF', value: formatCents(s.rdfCents) },
            {
              label: 'Revenue',
              hint: s.pennyDriftCents ? `includes ${formatCents(s.pennyDriftCents)} penny drift` : undefined,
              value: formatCents(s.revenueCents),
            },
            { label: 'Charge total', value: formatCents(s.chargeTotalCents), total: true },
          ]}
        />
        <div className="mt-3">
          <FieldGrid>
            <Field label="Chef liability recognized">{formatCents(s.chefLiabilityRecognizedCents)}</Field>
            <Field label="Taxable base">{formatCents(s.taxableBaseCents)}</Field>
            <Field label="Target quarter">{formatCents(s.targetQuarterCents)}</Field>
            <Field label="Payment method">{s.paymentMethod && humanize(s.paymentMethod)}</Field>
          </FieldGrid>
        </div>
      </Section>

      <Section title="Config at tax calc">
        <FieldGrid>
          <Field label="Effective tax rate">{formatRate(s.effectiveTaxRateAtCalc, 'fraction', 3)}</Field>
          <Field label="Buffer fee">
            <EffectiveValue value={formatRate(s.bufferFeePercentageAtCalc, 'fraction')} source={s.bufferFeeSource} />
          </Field>
          <Field label="Round-up increment">{formatCents(s.roundUpIncrementAtCalc)}</Field>
          <Field label="Minimum revenue">{formatCents(s.minRevenueAtCalc)}</Field>
          <Field label="Fee in tax base">{s.feeInTaxBaseModeAtCalc && humanize(s.feeInTaxBaseModeAtCalc)}</Field>
          <Field label="Food tax code">
            <span className="font-mono text-xs">{s.foodTaxCodeAtCalc}</span>
          </Field>
          <Field label="Locale at calc">{locale?.data ? localeLabel(locale.data) : s.localeIdAtCalc}</Field>
          <Field label="Fulfillment type">{humanize(s.fulfillmentType)}</Field>
          <Field label="Active registrations" wide>
            {(s.activeRegistrationIds ?? []).length ? (
              <span className="font-mono text-xs">{(s.activeRegistrationIds ?? []).join(', ')}</span>
            ) : undefined}
          </Field>
          <Field label="From (chef)" wide>
            {[s.fromAddressStreet, s.fromAddressCity, s.fromAddressStateProvince, s.fromAddressPostalCode]
              .filter(Boolean)
              .join(', ')}
          </Field>
          <Field label="To (eater)" wide>
            {[s.toAddressStreet, s.toAddressCity, s.toAddressStateProvince, s.toAddressPostalCode]
              .filter(Boolean)
              .join(', ')}
          </Field>
        </FieldGrid>
      </Section>

      <Section title="Settlement">
        <FieldGrid>
          <Field label="Captured">{formatDateTime(s.createdAt)}</Field>
          <Field label="Settlement date">{s.stripeSettlementDate && formatDateTime(s.stripeSettlementDate)}</Field>
          <Field label="Stripe-reported fee">
            {s.stripeReportedProcessingFeeCents !== undefined && formatCents(s.stripeReportedProcessingFeeCents)}
          </Field>
          <Field label="Settlement">
            {s.stripeSettlementId && <CopyableId id={s.stripeSettlementId} label="Settlement ID" />}
          </Field>
          <Field label="Payout">{s.stripePayoutId && <CopyableId id={s.stripePayoutId} label="Payout ID" />}</Field>
          <Field label="Chef balance entry">
            {s.listerBalanceId && <CopyableId id={s.listerBalanceId} label="Balance entry ID" />}
          </Field>
          <Field label="Tax calc (sizing)">
            {s.stripeTaxCalcSizingCalculationId && (
              <CopyableId id={s.stripeTaxCalcSizingCalculationId} label="Calculation ID" />
            )}
          </Field>
          <Field label="Tax calc (collected)">
            {s.stripeTaxCalcCollectedCalculationId && (
              <CopyableId id={s.stripeTaxCalcCollectedCalculationId} label="Calculation ID" />
            )}
          </Field>
        </FieldGrid>
      </Section>

      {(!isEmptyObject(s.originalAuthorized) || !isEmptyObject(s.cancellationBreakdown)) && (
        <Section title="Cancellation">
          <FieldGrid>
            <Field label="Originally authorized" wide>
              {!isEmptyObject(s.originalAuthorized) && <KeyValues value={s.originalAuthorized} />}
            </Field>
            <Field label="Cancellation breakdown" wide>
              {!isEmptyObject(s.cancellationBreakdown) && <KeyValues value={s.cancellationBreakdown} />}
            </Field>
          </FieldGrid>
        </Section>
      )}
    </>
  );
}
